import React, { useEffect, useState } from 'react';
import { cameraService } from '../../../services/central-api/cameras';
import { StyleProp, View, ViewStyle } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Map, { MapMouseEvent } from '../../../components/Map';
import { Slider } from '@miblanchard/react-native-slider';
import MyAppText from '../../../components/MyAppText';
import { openInNewTab } from '../../../services/navigation-assistant';

interface Props {
    referenceCamera: Camera;
    setCameraId: React.Dispatch<React.SetStateAction<string>>;
    style?: StyleProp<ViewStyle>;
}

function OtherCamerasList({ referenceCamera, setCameraId }: Props) {
    const { styles, theme } = useStyles(styleSheet);

    const [cameras, setCameras] = useState<CamerasWithinRadiusList[]>([]);
    const [selectedRadius, setSelectedRadius] = useState(0.5);
    const location = { lat: Number(referenceCamera.latitude), lng: Number(referenceCamera.longitude) };

    async function getItems() {
        try {
            const cameras = await cameraService.getCamerasWithinRadiusList(referenceCamera.id, selectedRadius);
            setCameras(cameras);
        } catch (err) {
            console.error(err);
        }
    }

    function pinClick(pin: Marker, event: MapMouseEvent) {
        if (pin?.metadata?.camera?.id) {
            const isModifierClick = event.domEvent.button === 1 || event.domEvent.ctrlKey || event.domEvent.metaKey;

            if (isModifierClick) {
                openInNewTab('Cameras', { screen: 'CameraPlayer', params: { id: pin.metadata.camera.id } });
                return;
            }
            setCameraId(pin.metadata.camera.id);
        }
    }

    useEffect(() => {
        getItems();
    }, [selectedRadius]);

    return (
        <View style={styles.container}>
            {cameras.length > 0 && (
                <View style={{ flex: 1 }}>
                    <Map
                        pinnedMarker={location}
                        cameraPositions={cameras.map(camera => ({ ...camera, id: camera.id, latitude: Number(camera.latitude), longitude: Number(camera.longitude), type: camera.type, hasLpr: camera.hasLpr }))}
                        draggable
                        pinClick={pinClick}
                        canManagePointsOfInterest={false}
                        mapControls={false}
                        radius={selectedRadius}
                    />
                    <View style={{ width: '100%' }}>
                        <Slider
                            animateTransitions
                            minimumValue={0.1}
                            maximumValue={3}
                            minimumTrackTintColor={theme.colors.neutral['400']}
                            thumbTintColor={theme.colors.neutral['400']}
                            maximumTrackTintColor={theme.colors.neutral['300']}
                            value={selectedRadius}
                            step={0.1}
                            onValueChange={(value) => setSelectedRadius(value[0])}
                        />
                        <View style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'row' }}>
                            <MyAppText style={{ fontWeight: 'bold' }}>{`${selectedRadius.toFixed(2)}km`}</MyAppText>
                        </View>
                    </View>
                </View>
            )}
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flex: 1,
        flexGrow: 1
    },
    title: {
        fontSize: 16,
        fontWeight: 'bold',
        color: theme.colors.labelColor
    },
    item: (hovered: boolean, selected: boolean) => ({
        fontSize: 14,
        color: theme.colors.labelColor,
        width: '100%',
        backgroundColor: hovered || selected ? theme.colors.backgrounds['BG-1'] : 'transparent',
        padding: 5,
    }),
    items: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        padding: 10,
        overflowY: 'scroll',
        maxHeight: 800,
        marginTop: 10,
    },
    item_container: {
        width: '100%',
    },
    item_content: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
    }
}));


export default React.memo(OtherCamerasList);
